import Button from '@atlaskit/button/default/button';
import LinkButton from '@atlaskit/button/link';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import Image from '@atlaskit/image/image';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';
import { type ReactNode, useState } from 'react';
import { useIntl } from 'react-intl';

import codeReviewTickImg from '../../assets/code-review-tick.svg?url';
import { WarningIcon } from '../../assets/warning';

import { CreditConfirmationModal } from './confirmation-modal';
import messages from './messages';
import { useCreditDetails } from './use-credit-details';
import { useCreditRequest } from './use-credit-request';

const styles = cssMap({
	page: {
		minHeight: '100vh',
		justifyContent: 'center',
		alignItems: 'center',
		textAlign: 'center',
		paddingBlock: token('space.400'),
		paddingInline: token('space.200'),
	},
	content: { maxWidth: '40rem' },
});

export const CreditRequestPage = ({ cloudId }: { cloudId: string }) => {
	const { formatMessage } = useIntl();
	const { lookup, retry } = useCreditDetails(cloudId);
	const request = useCreditRequest(cloudId);
	const [confirming, setConfirming] = useState(false);
	const details = lookup.status === 'loaded' ? lookup.details : null;
	const sent = request.status === 'sent';
	const unavailable = request.status === 'unavailable' || (lookup.status === 'loaded' && !details);
	const available = details && !details.denied;
	const method = !unavailable && !sent ? details?.method : null;
	let { title } = messages;
	let { description } = messages;
	let illustration: ReactNode = null;
	if (sent) {
		title = messages.sent;
		description = messages.sentDescription;
		illustration = <Image src={codeReviewTickImg} alt="" />;
	} else if (unavailable) {
		title = messages.unavailable;
		description = messages.unavailableDescription;
	} else if (available) {
		title = messages.available;
		description = messages.availableDescription;
		illustration = <Image src={codeReviewTickImg} alt="" />;
	} else if (details?.denied) {
		illustration = <WarningIcon color={token('color.icon.accent.red')} />;
	}
	return (
		<Stack space="space.200" xcss={styles.page}>
			<Box xcss={styles.content}>
				<Stack space="space.200" alignInline="center">
					{illustration}
					<Heading size="xxlarge">{formatMessage(title)}</Heading>
					<Box role="status">
						<Text as="p">{formatMessage(description)}</Text>
						{details?.denied && details.resetsAt !== null && !sent && (
							<Text as="p">{formatMessage(messages.reset, { resetDate: details.resetsAt })}</Text>
						)}
					</Box>
					{lookup.status === 'loading' && <Spinner label={formatMessage(messages.loading)} />}
					{lookup.status === 'failed' && (
						<>
							<Box role="alert">
								<Text>{formatMessage(messages.lookupFailed)}</Text>
							</Box>
							<Button onClick={retry}>{formatMessage(messages.retry)}</Button>
						</>
					)}
					{method?.mode === 'REDIRECT_URL' && method.redirectUrl ? (
						<>
							<Text as="p">{formatMessage(messages.redirectConfirmation)}</Text>
							<LinkButton
								appearance="primary"
								href={method.redirectUrl}
								target="_blank"
								rel="noopener noreferrer"
							>
								{formatMessage(messages.request)}
							</LinkButton>
						</>
					) : (
						method && (
							<Button appearance="primary" onClick={() => setConfirming(true)}>
								{formatMessage(messages.request)}
							</Button>
						)
					)}
					<LinkButton href="https://github.com">{formatMessage(messages.back)}</LinkButton>
				</Stack>
			</Box>
			{confirming && method && method.mode !== 'REDIRECT_URL' && (
				<CreditConfirmationModal
					sending={request.status === 'sending'}
					rateLimited={request.rateLimited}
					error={request.error}
					onClose={() => setConfirming(false)}
					onConfirm={request.send}
				/>
			)}
		</Stack>
	);
};
